import type {
  EmployerFootprintAdminViewDto,
  EmployerVerificationDetail,
} from '../../api/employerVerificationDashboardApi';
import type { ReviewReasonEvidence } from './reviewReason.types';
import {
  buildSectorMismatchIssueStatement,
  readObservedEmployerSector,
} from './employerObservedSector';
import { readRoleMismatchContext } from './roleMismatchFindingPresentation';

export type AiRoleSectorAdjudicatorView = {
  issueStatement: string;
  dataPoints: ReviewReasonEvidence[];
  footerNote: string | null;
  advisoryLabel: string | null;
  advisorySeverity: 'warning' | 'success' | 'default';
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function extractQuoted(text: string, label: RegExp): string | null {
  const match = text.match(label);
  if (!match?.[1]) return null;
  return match[1].replace(/\\"/g, '"').trim() || null;
}

function parseAdvisoryFromReason(reason: string): {
  suggested: 'PASS' | 'REVIEW';
  confidencePct: number | null;
} | null {
  const match = reason.match(/\(Advisory\s+(PASS|REVIEW),\s*(\d+)%\)/i);
  if (!match?.[1]) return null;
  const suggested = match[1].trim().toUpperCase();
  if (suggested !== 'PASS' && suggested !== 'REVIEW') return null;
  return {
    suggested,
    confidencePct: match[2] ? Number.parseInt(match[2], 10) : null,
  };
}

function buildDataPoints(input: {
  declaredIndustry: string | null;
  observedSector: string | null;
  employerName: string | null;
  jobTitle: string | null;
}): ReviewReasonEvidence[] {
  const rows: ReviewReasonEvidence[] = [];
  if (input.declaredIndustry) {
    rows.push({ label: 'Customer declared', value: input.declaredIndustry, tone: 'declared' });
  }
  if (input.observedSector) {
    rows.push({
      label: 'Employer sector (observed)',
      value: input.observedSector,
      tone: 'observed',
    });
  }
  if (input.employerName) {
    rows.push({ label: 'Employer', value: input.employerName, tone: 'employer' });
  }
  if (input.jobTitle) {
    rows.push({ label: 'Role', value: input.jobTitle, tone: 'role' });
  }
  return rows.slice(0, 4);
}

/** Structured adjudicator view for AI Role Sector Analysis checkpoint popover. */
export function buildAiRoleSectorAdjudicatorView(input: {
  reason: string;
  detail: EmployerVerificationDetail;
  footprint?: EmployerFootprintAdminViewDto | null;
}): AiRoleSectorAdjudicatorView {
  const reason = input.reason.trim();
  const evidence = asRecord(input.detail.evidence);
  const ai = asRecord(evidence?.aiRoleSectorAnalysis);

  const creditMessage =
    readString(ai?.creditAdjudicatorMessage) ??
    reason.match(/Adjudicator:\s*(.+)$/is)?.[1]?.trim() ??
    null;

  const roleCtx = readRoleMismatchContext({
    detail: input.detail,
    footprint: input.footprint,
    declaredEmployer: input.detail.employer?.legalName ?? null,
  });

  const observedSector = readObservedEmployerSector({
    detail: input.detail,
    footprint: input.footprint,
    aiCreditMessage: creditMessage,
    checkpointReason: reason,
  });

  const declaredFromReason =
    extractQuoted(reason, /Declared industry\s+"([^"]+)"/i) ??
    extractQuoted(reason, /declared industry\s+"([^"]+)"/i);

  const employerFromReason =
    extractQuoted(reason, /employer\s+"([^"]+)"/i) ??
    extractQuoted(reason, /employer\s+'([^']+)'/i);

  const declaredIndustry = roleCtx.declaredIndustry ?? declaredFromReason;
  const employerName = roleCtx.employerName ?? employerFromReason;
  const jobTitle = roleCtx.jobTitle;

  const suggestedFromAi = readString(ai?.suggestedOutcome)?.toUpperCase();
  const confidenceFromAi =
    typeof ai?.confidence === 'number' && Number.isFinite(ai.confidence)
      ? Math.round(ai.confidence * 100)
      : null;

  const advisoryFromReason = parseAdvisoryFromReason(reason);
  const suggested =
    suggestedFromAi === 'PASS' || suggestedFromAi === 'REVIEW'
      ? suggestedFromAi
      : advisoryFromReason?.suggested ?? null;
  const confidence = confidenceFromAi ?? advisoryFromReason?.confidencePct ?? null;

  const dataPoints = buildDataPoints({
    declaredIndustry,
    observedSector,
    employerName,
    jobTitle,
  });

  const advisoryLabel =
    suggested === 'REVIEW' || suggested === 'PASS'
      ? `Advisory ${suggested}${confidence != null ? ` · ${confidence}% confidence` : ''}`
      : null;

  return {
    issueStatement: buildSectorMismatchIssueStatement({
      declaredIndustry,
      observedSector,
      employerName,
    }),
    dataPoints,
    footerNote:
      'Automated pre-read only — does not change the verification outcome.',
    advisoryLabel,
    advisorySeverity: suggested === 'PASS' ? 'success' : suggested === 'REVIEW' ? 'warning' : 'default',
  };
}
